// modules/elearning/classes/ClassFilter.jsx
import React, { useEffect, useState } from "react";
import {
  Search,
  RotateCw,
  SlidersHorizontal,
  Plus,
  MoreVertical,
  ChevronDown,
  FileSpreadsheet,
  UploadCloud,
  DownloadCloud,
  Eraser,
  BookOpen,
  Activity,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import { exportClassesCSV } from "../../../api/elearning/classes/classes";
import { showErrorToast } from "../../../components/ConfirmDialog";
import { hasPermission } from "../../../utils/permissionUtils";
import MobileAdvancedFilterSheet from "../../../components/ui/MobileAdvancedFilterSheet";

const ClassFilter = ({ onNewClick, onImportExcelClick, onFilterChange }) => {
  const [advanced, setAdvanced] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [courses, setCourses] = useState([]);
  const [filters, setFilters] = useState({
    keyword: "",
    course_id: "",
    is_active: "",
  });

  const isFiltering = Object.values(filters).some((v) => v !== "");

  // ✅ Responsive detector
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Load danh sách khóa học
  useEffect(() => {
    const fetchCourses = async () => {
      try {
        const data = await getAllCoursesNoPaging();
        setCourses(data.records || data);
      } catch (error) {
        console.error("Lỗi khi tải danh sách khóa học:", error);
      }
    };
    fetchCourses();
  }, []);

  // ✅ Debounce keyword
  useEffect(() => {
    const timer = setTimeout(() => onFilterChange?.(filters), 500);
    return () => clearTimeout(timer);
  }, [filters.keyword]);

  const updateFilter = (name, value) => {
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    if (name !== "keyword") onFilterChange?.(updated);
  };

  const clearFilters = () => {
    const cleared = { keyword: "", course_id: "", is_active: "" };
    setFilters(cleared);
    onFilterChange?.(cleared);
  };

  const openGuide = () => {
    const guideUrl = `${window.location.origin}/guides/elearning-classes-guide.html`;
    window.open(guideUrl, "_blank", "noopener,noreferrer");
  };

  const downloadCSV = async () => {
    try {
      const blob = await exportClassesCSV();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "classes_export.csv";
      link.click();
    } catch (error) {
      showErrorToast("Không thể export dữ liệu.");
    }
  };

  // ================= RENDER COMPONENTS =================
  const ActionButton = ({ icon: Icon, onClick, title, active }) => (
    <button
      onClick={onClick}
      title={title}
      className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all duration-200 
        ${active
          ? "bg-red-50 border-red-200 text-red-600 shadow-sm"
          : "bg-white border-gray-200 text-gray-500 hover:border-red-400 hover:text-red-500"
        }`}
    >
      <Icon size={18} />
    </button>
  );

  const SelectBox = ({ label, value, options, onChange, icon: Icon }) => (
    <div className="flex flex-col gap-1.5 w-full">
      <label className="text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1 flex items-center gap-1.5">
        {Icon && <Icon size={12} />} {label}
      </label>
      <div className="relative group">
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="appearance-none w-full bg-white border border-gray-200 text-gray-700 py-2.5 px-4 pr-10 rounded-xl text-sm
                     focus:outline-none focus:border-red-500 focus:ring-4 focus:ring-red-500/5 transition-all cursor-pointer font-medium shadow-sm"
        >
          <option value="">Tất cả {label}</option>
          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none group-hover:text-red-500 transition-colors" />
      </div>
    </div>
  );

  // ================= MOBILE UI =================
  if (isMobile) {
    return (
      <div className="mb-6 space-y-4">
        <div className="flex gap-2 text-left">
          <div className="relative flex-1 group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm lớp, đơn vị hoặc trường học..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-white border border-gray-200 pl-11 pr-4 py-3 rounded-2xl focus:outline-none focus:border-red-500 shadow-sm transition-all outline-none"
            />
          </div>
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} />
          {hasPermission("elearning.classes", "C") && onImportExcelClick && (
            <ActionButton
              icon={FileSpreadsheet}
              onClick={() => onImportExcelClick()}
              title="Nhập lớp từ Excel"
            />
          )}
        </div>

        <MobileAdvancedFilterSheet
          open={advanced}
          onClose={() => setAdvanced(false)}
          title="Bộ lọc lớp học"
          footer={
            <button
              type="button"
              onClick={() => setAdvanced(false)}
              className="w-full py-4 bg-red-600 text-white rounded-2xl font-bold shadow-lg shadow-red-200"
            >
              ÁP DỤNG
            </button>
          }
        >
          <div className="grid grid-cols-1 gap-4">
            <SelectBox label="Khóa học" value={filters.course_id} options={courses.map(c => ({ value: c.id, label: c.name }))} onChange={(v) => updateFilter("course_id", v)} icon={BookOpen} />
            <SelectBox label="Trạng thái" value={filters.is_active} options={[{ value: "1", label: "Hoạt động" }, { value: "0", label: "Tạm ngưng" }]} onChange={(v) => updateFilter("is_active", v)} icon={Activity} />
          </div>
        </MobileAdvancedFilterSheet>
      </div>
    );
  }

  // ================= DESKTOP UI =================
  return (
    <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm mb-6 text-left relative">
      <div className="flex items-center justify-between gap-4">
        {/* Nhóm tìm kiếm & nút chức năng */}
        <div className="flex items-center gap-3 flex-1">
          <div className="relative w-full max-w-sm group">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-red-500 transition-colors" size={18} />
            <input
              type="text"
              placeholder="Tìm lớp, đơn vị hoặc trường học..."
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              className="w-full bg-gray-50 border-transparent border-2 focus:bg-white focus:border-red-500 pl-11 pr-4 py-2.5 rounded-2xl outline-none transition-all font-medium text-gray-700"
            />
          </div>

          <ActionButton icon={RotateCw} onClick={clearFilters} title="Làm mới" />
          <ActionButton icon={SlidersHorizontal} onClick={() => setAdvanced(!advanced)} active={advanced} title="Lọc nâng cao" />

          {isFiltering && (
            <button onClick={clearFilters} className="text-xs font-bold text-red-500 hover:text-red-700 flex items-center gap-1 uppercase tracking-wider transition-all ml-2">
              <Eraser size={14} /> Xóa lọc
            </button>
          )}
        </div>

        {/* Nhóm hành động chính */}
        <div className="flex items-center gap-3">
          {hasPermission("elearning.classes", "C") && onImportExcelClick && (
            <button
              type="button"
              onClick={() => onImportExcelClick()}
              className="flex items-center gap-2 px-5 py-2.5 bg-white border-2 border-red-200 text-red-700 rounded-full font-bold hover:bg-red-50 shadow-sm transition-all active:scale-95 uppercase text-xs"
            >
              <FileSpreadsheet size={18} />
              Nhập Excel
            </button>
          )}

          {/* Nút Thêm mới */}
          {hasPermission("elearning.classes", "C") && (
            <button
              onClick={onNewClick}
              className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-red-700 to-red-800 text-white rounded-full font-bold hover:shadow-lg hover:shadow-red-100 transition-all active:scale-95 uppercase text-xs tracking-wider"
            >
              <Plus size={18} />
              Thêm lớp học
            </button>
          )}

          <div className="relative">
            <button
              onClick={() => setMoreOpen(!moreOpen)}
              className={`h-11 w-11 flex items-center justify-center rounded-xl border transition-all ${moreOpen ? 'bg-gray-100 border-gray-300 shadow-inner' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            >
              <MoreVertical size={18} />
            </button>

            <AnimatePresence>
              {moreOpen && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  className="absolute right-0 mt-3 w-56 bg-white border border-gray-100 rounded-[1.5rem] shadow-2xl z-50 p-2 overflow-hidden"
                  onMouseLeave={() => setMoreOpen(false)}
                >
                  {[
                    { icon: FileSpreadsheet, label: "Template (Excel)", onClick: () => downloadTemplate() },
                    {
                      icon: UploadCloud,
                      label: "Import (Excel)",
                      onClick: () => onImportExcelClick?.(),
                    },
                    { icon: DownloadCloud, label: "Export Data", onClick: downloadCSV },
                    { icon: BookOpen, label: "Hướng dẫn sử dụng", onClick: openGuide },
                  ].map((item) => (
                    <button
                      key={item.label}
                      className="flex items-center gap-3 w-full px-4 py-3 text-xs font-bold text-gray-600 hover:bg-red-50 hover:text-red-600 rounded-xl transition-all"
                      onClick={() => { item.onClick(); setMoreOpen(false); }}
                    >
                      <item.icon size={16} />
                      {item.label.toUpperCase()}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Vùng lọc nâng cao */}
      <AnimatePresence>
        {advanced && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-visible"
          >
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-100">
              <SelectBox
                label="Khóa học"
                value={filters.course_id}
                options={courses.map(c => ({ value: c.id, label: c.name }))}
                onChange={(v) => updateFilter("course_id", v)}
                icon={BookOpen}
              />
              <SelectBox
                label="Trạng thái"
                value={filters.is_active}
                options={[{ value: "1", label: "Active" }, { value: "0", label: "Inactive" }]}
                onChange={(v) => updateFilter("is_active", v)}
                icon={Activity}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// 📄 Hàm tải template CSV (Giữ nguyên logic của bạn)
const downloadTemplate = () => {
  const link = document.createElement("a");
  link.href = `${process.env.PUBLIC_URL || ""}/templates/class_template.csv`;
  link.download = "mau_nhap_lop_hoc.csv";
  link.click();
};

export default ClassFilter;